import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { SearchWidget } from "@/components/SearchWidget";
import { HeroMark } from "@/components/Logo";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import { useLanguage } from "@/context/LanguageContext";
import { ShieldCheck, Wallet, Sparkles, Search, Car, CheckCircle2, Bus, ArrowRight, Star, Users } from "lucide-react";

const popularRoutes = [
  { from: "Bangkok", to: "Chiang Mai", price: 550 },
  { from: "Bangkok", to: "Phuket", price: 850 },
  { from: "Bangkok", to: "Chonburi", price: 150 },
  { from: "Chiang Mai", to: "Chiang Rai", price: 180 },
  { from: "Bangkok", to: "Khon Kaen", price: 400 },
  { from: "Phuket", to: "Krabi", price: 200 },
];

const Home = () => {
  const [stats, setStats] = useState({ rides: 0, drivers: 0, bus_trips: 0 });
  const { t } = useLanguage();
  useEffect(() => { api.get("/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 hero-backdrop pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 lg:pt-16 pb-4">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <Badge className="bg-[rgba(34,211,238,0.12)] text-[#A5F3FC] border border-[rgba(34,211,238,0.22)] gap-1 mb-4"><Sparkles size={12} /> {t("home.badge")}</Badge>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
                {t("home.title1")}<br /><span className="text-[#22D3EE]">{t("home.title2")}</span>
              </h1>
              <p className="mt-4 text-lg text-secondary-cosmic max-w-lg">Find affordable rides between cities with trusted drivers, or publish your own trip and fill those empty seats. Fun, friendly, out of this world.</p>
              <div className="mt-5 flex flex-wrap gap-4 text-sm text-secondary-cosmic">
                <span className="flex items-center gap-1"><ShieldCheck size={16} className="text-[#34D399]" /> Verified profiles</span>
                <span className="flex items-center gap-1"><Wallet size={16} className="text-[#F7C948]" /> Best prices</span>
                <span className="flex items-center gap-1"><Star size={16} className="text-[#F7C948]" /> Rated drivers</span>
              </div>
            </motion.div>
            <motion.div className="flex justify-center lg:justify-end" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }}>
              <HeroMark />
            </motion.div>
          </div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }} className="mt-6">
            <SearchWidget />
          </motion.div>

          <div className="mt-6 flex flex-wrap gap-6 text-sm text-muted-cosmic">
            <span><span className="font-display text-xl text-white font-semibold">{stats.rides}+</span> live rides</span>
            <span><span className="font-display text-xl text-white font-semibold">{stats.drivers}+</span> cosmic drivers</span>
            <span><span className="font-display text-xl text-white font-semibold">{stats.bus_trips}+</span> bus trips</span>
          </div>
        </div>
      </section>

      {/* Popular routes */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-end justify-between mb-6">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold">{t("home.popular")}</h2>
          <Link to="/search" className="text-sm text-[#22D3EE] flex items-center gap-1 hover:gap-2 transition-all">See all <ArrowRight size={14} /></Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {popularRoutes.map((r, i) => (
            <Link key={i} to={`/search?from=${r.from}&to=${r.to}`}>
              <Card className="glass p-5 hover:-translate-y-[2px] transition-[transform,box-shadow,border-color] duration-200 hover:border-white/20 hover:glow-cyan cursor-pointer h-full">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 font-display text-lg"><span>{r.from}</span><ArrowRight size={16} className="text-[#7C3AED]" /><span>{r.to}</span></div>
                  <Car size={18} className="text-[#22D3EE]" />
                </div>
                <div className="mt-3 text-sm text-muted-cosmic">from <span className="text-[#FFF7D6] font-semibold">{r.price} ฿</span></div>
              </Card>
            </Link>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h2 className="font-display text-2xl sm:text-3xl font-semibold text-center">{t("home.how")}</h2>
        <div className="grid md:grid-cols-3 gap-6 mt-8">
          {[
            { icon: Search, title: "Search your ride", desc: "Enter your route and date. Compare trusted drivers, prices and reviews in seconds." },
            { icon: CheckCircle2, title: "Book instantly", desc: "Reserve your seat with instant booking. No waiting for the mothership." },
            { icon: Users, title: "Travel together", desc: "Meet your driver, hop in, and share the journey (and the costs)." },
          ].map((s, i) => (
            <Card key={i} className="glass p-6 text-center">
              <div className="mx-auto w-12 h-12 rounded-full grid place-items-center glass-solid glow-violet mb-4"><s.icon size={22} className="text-[#22D3EE]" /></div>
              <h3 className="font-display text-lg font-semibold">{s.title}</h3>
              <p className="text-sm text-muted-cosmic mt-2">{s.desc}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* Bus teaser */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card className="glass p-8 flex flex-col md:flex-row items-center gap-6 justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl grid place-items-center glass-solid glow-gold"><Bus size={26} className="text-[#F7C948]" /></div>
            <div>
              <h3 className="font-display text-xl font-semibold">{t("home.busTitle")}</h3>
              <p className="text-sm text-muted-cosmic mt-1">Comfy long-distance coaches with Wi-Fi, power and extra legroom.</p>
            </div>
          </div>
          <Link to="/bus"><Button className="bg-[hsl(var(--primary))] text-white glow-violet hover:glow-cyan gap-2">Explore bus tickets <ArrowRight size={16} /></Button></Link>
        </Card>
      </section>
    </div>
  );
};

export default Home;
